/**
 * Full lemon-law / warranty rights notices by state (Magnuson-Moss + state statutes).
 */

import { formatMoney } from "./calc.js";

function esc(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Statute citation, agency contact, and short rights summary per jurisdiction. */
export const LEMON_LAW_BY_STATE = {
  AL: {
    statute: "Ala. Code §§ 8-20A-1 et seq. (Motor Vehicle Lemon Law Rights)",
    agency: "Alabama Attorney General — Consumer Protection · 800-392-5658 · ago.alabama.gov",
    summary:
      "New motor vehicles that cannot be repaired after a reasonable number of attempts may qualify for repurchase or replacement under Alabama’s lemon law, subject to statutory notice and eligibility limits.",
  },
  AK: {
    statute: "Alaska Stat. §§ 45.45.300 – 45.45.360 (Motor Vehicle Warranties)",
    agency: "Alaska Attorney General — Consumer Protection · 907-269-5200 · law.alaska.gov",
    summary:
      "Alaska’s motor vehicle warranty law provides remedies when a new vehicle has a nonconformity that substantially impairs use, value, or safety after reasonable repair attempts.",
  },
  AZ: {
    statute: "Ariz. Rev. Stat. §§ 44-1261 et seq. (Motor Vehicle Warranties)",
    agency: "Arizona Attorney General — Consumer Protection · 602-542-5763 · azag.gov",
    summary:
      "Arizona lemon-law rights may apply to new motor vehicles with substantial nonconformities that are not repaired after a reasonable number of attempts within the statutory period.",
  },
  AR: {
    statute: "Ark. Code Ann. §§ 4-90-401 et seq. (New Motor Vehicle Quality Assurance Act)",
    agency: "Arkansas Attorney General — Consumer Protection · 800-482-8982 · arkansasag.gov",
    summary:
      "Arkansas provides repurchase/replacement remedies for qualifying new vehicles that remain defective after reasonable repair attempts and required notice.",
  },
  CA: {
    statute: "Cal. Civ. Code §§ 1793.2 – 1793.25 (Song-Beverly Consumer Warranty Act / lemon law)",
    agency: "California DMV / Department of Consumer Affairs · 800-952-5210 · dca.ca.gov",
    summary:
      "California’s Song-Beverly Act provides strong new-vehicle warranty remedies, including repurchase or replacement when a defect cannot be repaired after a reasonable number of attempts.",
  },
  CO: {
    statute: "Colo. Rev. Stat. §§ 42-10-101 et seq. (Motor Vehicle Warranties)",
    agency: "Colorado Attorney General — Consumer Protection · 800-222-4444 · coag.gov",
    summary:
      "Colorado lemon-law coverage addresses new motor vehicles with defects that substantially impair use or market value after reasonable repair attempts.",
  },
  CT: {
    statute: "Conn. Gen. Stat. §§ 42-179 et seq. (Lemon Law)",
    agency: "Connecticut DMV Lemon Law Unit · 860-263-5400 · portal.ct.gov/dmv",
    summary:
      "Connecticut’s lemon law includes a state arbitration program for qualifying new vehicles that cannot be repaired after reasonable attempts.",
  },
  DE: {
    statute: "Del. Code Ann. tit. 6, §§ 5001 et seq. (Automobile Warranties)",
    agency: "Delaware Attorney General — Fraud & Consumer Protection · 800-220-5424 · attorneygeneral.delaware.gov",
    summary:
      "Delaware provides remedies for new automobiles with substantial defects not cured after a reasonable number of repair attempts.",
  },
  DC: {
    statute: "D.C. Code §§ 50-501 et seq. (Automobile Consumer Protection Act)",
    agency: "D.C. Department of Licensing and Consumer Protection · 202-442-4400 · dlcp.dc.gov",
    summary:
      "District of Columbia lemon-law rights may require repurchase or replacement of a new vehicle with persistent substantial nonconformities.",
  },
  FL: {
    statute: "Fla. Stat. §§ 681.10 et seq. (Motor Vehicle Warranty Enforcement Act)",
    agency: "Florida Department of Agriculture and Consumer Services — Lemon Law · 800-321-5366 · fdacs.gov",
    summary:
      "Florida’s lemon law provides manufacturer repurchase/replacement and a state-administered dispute program for qualifying new vehicles.",
  },
  GA: {
    statute: "Ga. Code Ann. §§ 10-1-780 et seq. (Motor Vehicle Warranty Rights Act)",
    agency: "Georgia Governor’s Office of Consumer Protection · 404-651-8600 · consumer.georgia.gov",
    summary:
      "Georgia lemon-law remedies apply to new motor vehicles with substantial defects not repaired after reasonable attempts within the statutory window.",
  },
  HI: {
    statute: "Haw. Rev. Stat. §§ 481I-1 et seq. (Motor Vehicle Express Warranty Enforcement)",
    agency: "Hawaii Office of Consumer Protection · 808-586-2630 · cca.hawaii.gov/ocp",
    summary:
      "Hawaii provides lemon-law relief for new vehicles that cannot be conformed to warranty after a reasonable number of repair attempts.",
  },
  ID: {
    statute: "Idaho Code §§ 48-901 et seq. (New Motor Vehicle Warranties)",
    agency: "Idaho Attorney General — Consumer Protection · 208-334-2424 · ag.idaho.gov",
    summary:
      "Idaho’s new motor vehicle warranty law provides remedies for substantial nonconformities after reasonable repair attempts.",
  },
  IL: {
    statute: "815 ILCS 380/1 et seq. (New Vehicle Buyer Protection Act)",
    agency: "Illinois Attorney General — Consumer Fraud · 800-386-5438 · illinoisattorneygeneral.gov",
    summary:
      "Illinois lemon-law rights may include refund or replacement when a new vehicle has defects that substantially impair use, value, or safety.",
  },
  IN: {
    statute: "Ind. Code §§ 24-5-13-1 et seq. (Motor Vehicle Protection Act)",
    agency: "Indiana Attorney General — Consumer Protection · 800-382-5516 · in.gov/attorneygeneral",
    summary:
      "Indiana provides lemon-law remedies for new vehicles with persistent substantial defects after reasonable repair attempts.",
  },
  IA: {
    statute: "Iowa Code §§ 322G.1 et seq. (Motor Vehicle Service Trade Practices / lemon provisions)",
    agency: "Iowa Attorney General — Consumer Protection · 515-281-5926 · iowaattorneygeneral.gov",
    summary:
      "Iowa law provides consumer remedies related to new motor vehicle warranty nonconformities and required disclosures.",
  },
  KS: {
    statute: "Kan. Stat. Ann. §§ 50-645 et seq. (Lemon Law)",
    agency: "Kansas Attorney General — Consumer Protection · 800-432-2310 · ag.ks.gov",
    summary:
      "Kansas lemon-law coverage addresses new vehicles that remain defective after a reasonable number of repair attempts.",
  },
  KY: {
    statute: "Ky. Rev. Stat. §§ 367.840 et seq. (Motor Vehicle Warranty Act)",
    agency: "Kentucky Attorney General — Consumer Protection · 502-696-5389 · ag.ky.gov",
    summary:
      "Kentucky provides repurchase/replacement remedies for qualifying new motor vehicles with substantial unrepaired defects.",
  },
  LA: {
    statute: "La. Rev. Stat. §§ 51:1941 et seq. (Motor Vehicle Warranty Act)",
    agency: "Louisiana Attorney General — Consumer Protection · 800-351-4889 · ag.state.la.us",
    summary:
      "Louisiana lemon-law rights apply to new vehicles with nonconformities that substantially impair use, value, or safety after reasonable repairs.",
  },
  ME: {
    statute: "Me. Rev. Stat. tit. 10, §§ 1161 et seq. (Maine Lemon Law)",
    agency: "Maine Attorney General — Consumer Protection · 800-436-2131 · maine.gov/ag",
    summary:
      "Maine’s lemon law provides remedies and a state arbitration option for qualifying new motor vehicles.",
  },
  MD: {
    statute: "Md. Code Ann., Com. Law §§ 14-1501 et seq. (Maryland Automotive Warranty Enforcement Act)",
    agency: "Maryland Motor Vehicle Administration / Consumer Protection · 410-768-7000 · mva.maryland.gov",
    summary:
      "Maryland lemon-law rights may require manufacturer repurchase or replacement after unsuccessful reasonable repair attempts.",
  },
  MA: {
    statute: "Mass. Gen. Laws ch. 90, § 7N½ (Lemon Law)",
    agency: "Massachusetts Office of Consumer Affairs & Business Regulation · 888-283-3757 · mass.gov/ocabr",
    summary:
      "Massachusetts lemon law provides refund or replacement remedies and manufacturer arbitration options for qualifying new vehicles.",
  },
  MI: {
    statute: "Mich. Comp. Laws §§ 257.1401 et seq. (New Motor Vehicle Warranties)",
    agency: "Michigan Attorney General — Consumer Protection · 877-765-8388 · michigan.gov/ag",
    summary:
      "Michigan provides lemon-law remedies when a new vehicle cannot be repaired to conform to warranty after reasonable attempts.",
  },
  MN: {
    statute: "Minn. Stat. §§ 325F.665 et seq. (Motor Vehicle Warranty Law)",
    agency: "Minnesota Attorney General — Consumer Division · 800-657-3787 · ag.state.mn.us",
    summary:
      "Minnesota lemon-law rights include manufacturer repurchase or replacement for substantial unrepaired nonconformities.",
  },
  MS: {
    statute: "Miss. Code Ann. §§ 63-17-151 et seq. (Motor Vehicle Warranty Enforcement Act)",
    agency: "Mississippi Attorney General — Consumer Protection · 800-281-4418 · ago.ms.gov",
    summary:
      "Mississippi’s Motor Vehicle Warranty Enforcement Act (§§ 63-17-151 et seq.) provides remedies when a new motor vehicle has a nonconformity that substantially impairs use, market value, or safety and is not repaired after a reasonable number of attempts within the statutory term/mileage limits. Manufacturer notice and informal dispute procedures may be required before court action.",
  },
  MO: {
    statute: "Mo. Rev. Stat. §§ 407.560 et seq. (New Motor Vehicle Warranties)",
    agency: "Missouri Attorney General — Consumer Protection · 800-392-8222 · ago.mo.gov",
    summary:
      "Missouri lemon-law remedies apply to new vehicles with substantial defects not cured after reasonable repair attempts.",
  },
  MT: {
    statute: "Mont. Code Ann. §§ 61-4-501 et seq. (New Motor Vehicle Warranties)",
    agency: "Montana Department of Justice — Office of Consumer Protection · 800-481-6896 · dojmt.gov",
    summary:
      "Montana provides lemon-law relief for new motor vehicles with persistent substantial nonconformities.",
  },
  NE: {
    statute: "Neb. Rev. Stat. §§ 60-2701 et seq. (Motor Vehicle Warranty Act)",
    agency: "Nebraska Attorney General — Consumer Protection · 800-727-6432 · ago.nebraska.gov",
    summary:
      "Nebraska lemon-law rights address new vehicles that cannot be repaired after a reasonable number of attempts.",
  },
  NV: {
    statute: "Nev. Rev. Stat. §§ 597.600 et seq. (Adjustment of Vehicle Warranty Claims)",
    agency: "Nevada Department of Motor Vehicles / Consumer Affairs · 702-486-7355 · dmvnv.com",
    summary:
      "Nevada provides consumer remedies related to new motor vehicle warranty nonconformities and required manufacturer procedures.",
  },
  NH: {
    statute: "N.H. Rev. Stat. Ann. §§ 357-D:1 et seq. (New Motor Vehicle Arbitration)",
    agency: "New Hampshire Department of Justice — Consumer Protection · 888-468-4454 · doj.nh.gov",
    summary:
      "New Hampshire’s lemon law includes arbitration procedures for qualifying new motor vehicle warranty disputes.",
  },
  NJ: {
    statute: "N.J. Stat. Ann. §§ 56:12-29 et seq. (Lemon Law)",
    agency: "New Jersey Division of Consumer Affairs — Lemon Law Unit · 973-504-6226 · njconsumeraffairs.gov",
    summary:
      "New Jersey lemon-law rights provide refund or replacement remedies and a state dispute process for qualifying new vehicles.",
  },
  NM: {
    statute: "N.M. Stat. Ann. §§ 57-16A-1 et seq. (Motor Vehicle Quality Assurance Act)",
    agency: "New Mexico Attorney General — Consumer Protection · 505-490-4060 · nmag.gov",
    summary:
      "New Mexico provides lemon-law remedies for new vehicles with substantial unrepaired defects after reasonable attempts.",
  },
  NY: {
    statute: "N.Y. Gen. Bus. Law § 198-a (New Car Lemon Law)",
    agency: "New York Department of State / DMV Lemon Law · 800-697-1220 · dos.ny.gov",
    summary:
      "New York’s new-car lemon law provides refund or replacement when a substantial defect persists after a reasonable number of repair attempts or days out of service.",
  },
  NC: {
    statute: "N.C. Gen. Stat. §§ 20-351 et seq. (New Motor Vehicles Warranties Act)",
    agency: "North Carolina Department of Justice — Consumer Protection · 877-566-7226 · ncdoj.gov",
    summary:
      "North Carolina lemon-law rights apply to new motor vehicles with substantial nonconformities not repaired after reasonable attempts.",
  },
  ND: {
    statute: "N.D. Cent. Code §§ 51-07-16 et seq. (Motor Vehicle Warranties)",
    agency: "North Dakota Attorney General — Consumer Protection · 800-472-2600 · attorneygeneral.nd.gov",
    summary:
      "North Dakota provides lemon-law remedies for qualifying new vehicles with persistent warranty nonconformities.",
  },
  OH: {
    statute: "Ohio Rev. Code §§ 1345.71 et seq. (Nonconforming New Motor Vehicle Law)",
    agency: "Ohio Attorney General — Consumer Protection · 800-282-0515 · ohioattorneygeneral.gov",
    summary:
      "Ohio lemon-law rights may require manufacturer repurchase or replacement after unsuccessful reasonable repair attempts.",
  },
  OK: {
    statute: "Okla. Stat. tit. 15, §§ 901 et seq. (Motor Vehicle Warranty Enforcement Act)",
    agency: "Oklahoma Attorney General — Consumer Protection · 405-521-3921 · oag.ok.gov",
    summary:
      "Oklahoma provides lemon-law remedies for new motor vehicles with substantial unrepaired defects.",
  },
  OR: {
    statute: "Or. Rev. Stat. §§ 646A.400 et seq. (Lemon Law)",
    agency: "Oregon Department of Justice — Consumer Protection · 877-877-9392 · doj.state.or.us",
    summary:
      "Oregon lemon-law coverage includes repurchase/replacement remedies and manufacturer informal dispute options.",
  },
  PA: {
    statute: "73 Pa. Stat. §§ 1951 et seq. (Automobile Lemon Law)",
    agency: "Pennsylvania Office of Attorney General — Bureau of Consumer Protection · 800-441-2555 · attorneygeneral.gov",
    summary:
      "Pennsylvania’s lemon law provides remedies for new vehicles that cannot be repaired after a reasonable number of attempts.",
  },
  RI: {
    statute: "R.I. Gen. Laws §§ 31-5.2-1 et seq. (Lemon Law)",
    agency: "Rhode Island Department of Attorney General — Consumer Protection · 401-274-4400 · riag.ri.gov",
    summary:
      "Rhode Island lemon-law rights address new vehicles with substantial defects remaining after reasonable repair attempts.",
  },
  SC: {
    statute: "S.C. Code Ann. §§ 56-28-10 et seq. (Motor Vehicle Express Warranty Act)",
    agency: "South Carolina Department of Consumer Affairs · 800-922-1594 · consumer.sc.gov",
    summary:
      "South Carolina provides lemon-law remedies for new motor vehicles with nonconformities that substantially impair use, market value, or safety after reasonable repairs.",
  },
  SD: {
    statute: "S.D. Codified Laws §§ 32-6D-1 et seq. (New Motor Vehicle Warranty)",
    agency: "South Dakota Attorney General — Consumer Protection · 800-300-1986 · atg.sd.gov",
    summary:
      "South Dakota lemon-law rights apply to new vehicles with substantial unrepaired warranty nonconformities.",
  },
  TN: {
    statute: "Tenn. Code Ann. §§ 55-24-101 et seq. (Motor Vehicle Warranty Act)",
    agency: "Tennessee Division of Consumer Affairs · 800-342-8385 · tn.gov/commerce",
    summary:
      "Tennessee provides lemon-law repurchase/replacement remedies for qualifying new motor vehicles.",
  },
  TX: {
    statute: "Tex. Occupations Code §§ 2301.601 et seq. (Lemon Law)",
    agency: "Texas Department of Motor Vehicles — Lemon Law · 888-368-4689 · txdmv.gov",
    summary:
      "Texas lemon-law and warranty performance rights are administered through TxDMV and may include repurchase, replacement, or repair orders after hearing.",
  },
  UT: {
    statute: "Utah Code §§ 13-20-1 et seq. (New Motor Vehicle Warranties Act)",
    agency: "Utah Division of Consumer Protection · 801-530-6601 · dcp.utah.gov",
    summary:
      "Utah lemon-law remedies apply to new motor vehicles with substantial defects not cured after reasonable repair attempts.",
  },
  VT: {
    statute: "Vt. Stat. Ann. tit. 9, §§ 4170 et seq. (Motor Vehicle Manufacturers, Distributors & Dealers Franchising / lemon provisions)",
    agency: "Vermont Attorney General — Consumer Assistance · 800-649-2424 · ago.vermont.gov",
    summary:
      "Vermont provides consumer remedies for new motor vehicle warranty nonconformities, including arbitration options where applicable.",
  },
  VA: {
    statute: "Va. Code Ann. §§ 59.1-207.9 et seq. (Virginia Motor Vehicle Warranty Enforcement Act)",
    agency: "Virginia Department of Agriculture and Consumer Services · 800-552-9963 · vdacs.virginia.gov",
    summary:
      "Virginia lemon-law rights provide refund or replacement for qualifying new vehicles with persistent substantial defects.",
  },
  WA: {
    statute: "Wash. Rev. Code §§ 19.118.005 et seq. (Motor Vehicle Warranties Act)",
    agency: "Washington Attorney General — Lemon Law Administration · 800-541-8898 · atg.wa.gov",
    summary:
      "Washington’s lemon law includes a state arbitration program for new vehicles with substantial unrepaired nonconformities.",
  },
  WV: {
    statute: "W. Va. Code §§ 46A-6A-1 et seq. (Lemon Law)",
    agency: "West Virginia Attorney General — Consumer Protection · 800-368-8808 · ago.wv.gov",
    summary:
      "West Virginia lemon-law remedies apply to new motor vehicles that cannot be repaired after a reasonable number of attempts.",
  },
  WI: {
    statute: "Wis. Stat. §§ 218.0171 et seq. (Repair, replacement and refund under new motor vehicle warranties)",
    agency: "Wisconsin Department of Transportation / DATCP Consumer Protection · 800-422-7128 · datcp.wi.gov",
    summary:
      "Wisconsin lemon-law rights provide repair, replacement, or refund remedies for qualifying new vehicles with substantial defects.",
  },
  WY: {
    statute: "Wyo. Stat. Ann. §§ 40-17-101 et seq. (Motor Vehicle Warranty Act)",
    agency: "Wyoming Attorney General — Consumer Protection · 307-777-7874 · ag.wyo.gov",
    summary:
      "Wyoming provides lemon-law remedies for new motor vehicles with substantial unrepaired warranty nonconformities.",
  },
};

function lemonInfo(code) {
  return (
    LEMON_LAW_BY_STATE[code] || {
      statute: `${code} motor vehicle warranty / lemon-law statutes as applicable`,
      agency: `${code} Attorney General — Consumer Protection / Motor Vehicle Agency`,
      summary:
        "State lemon-law rights may apply to new motor vehicles with substantial defects that cannot be repaired after a reasonable number of attempts. Confirm current statute text and eligibility with your state agency.",
    }
  );
}

/**
 * Full lemon-law notice HTML (header/signatures supplied by caller shell helpers via ctx hooks).
 * Expects ctx + helper functions passed in to avoid circular imports.
 */
export function renderLemonLawBody(ctx, helpers) {
  const { dealerHeader, buyerBox, vehicleBox, signatures } = helpers;
  const info = lemonInfo(ctx.stateCode);
  const price = formatMoney(ctx.deal?.price);

  return `${dealerHeader(ctx, "Lemon Law / Warranty Rights Notice")}
    <div class="grid2">${buyerBox(ctx)}${vehicleBox(ctx)}</div>
    <h2>1. Federal Warranty Rights — Magnuson-Moss Warranty Act</h2>
    <p class="legal">The federal Magnuson-Moss Warranty Act (15 U.S.C. §§ 2301 et seq.) governs written warranties on consumer products, including many motor vehicles. It does not create a new express warranty by itself, but it requires that written warranties be clear, prohibits certain disclaimer practices when a written warranty is given, and provides consumer remedies (including, in appropriate cases, costs and attorney fees) when a warrantor fails to comply. Implied warranties of merchantability or fitness may also apply under state law unless a valid “as-is” or other lawful disclaimer applies to a used-vehicle sale.</p>
    <p class="legal">If you purchased a service contract, extended warranty, or GAP product, those contracts are separate from the manufacturer’s written warranty. Keep all warranty booklets, repair orders, and this notice with worksheet <strong>${esc(
      ctx.deal?.id || "—"
    )}</strong> (selling price on worksheet: <strong>${esc(price)}</strong>).</p>

    <h2>2. ${esc(ctx.stateName)} Lemon Law Statute</h2>
    <p class="legal"><strong>Primary citation:</strong> ${esc(info.statute)}</p>
    <p class="legal">${esc(info.summary)}</p>
    <p class="legal"><strong>State agency / consumer contact:</strong> ${esc(info.agency)}</p>
    <p class="legal">Eligibility commonly depends on whether the vehicle is new (or otherwise covered), whether the defect substantially impairs use, value, or safety, whether the manufacturer/dealer received required notice and a reasonable number of repair attempts (or the vehicle was out of service a statutory number of days), and whether you used any required informal dispute settlement procedure. Used vehicles, commercial vehicles, motorcycles, RVs, and off-road equipment may be treated differently — verify coverage for your vehicle class.</p>

    <h2>3. Typical Rights If Covered</h2>
    <p class="legal">If your vehicle qualifies under ${esc(
      ctx.stateName
    )} law after unsuccessful repair attempts, available remedies often include:</p>
    <ul class="legal">
      <li>Manufacturer <strong>replacement</strong> with a comparable new vehicle; or</li>
      <li>Manufacturer <strong>repurchase / refund</strong> of the purchase price, less a reasonable allowance for use and plus certain collateral charges as the statute allows; and</li>
      <li>Participation in a qualifying <strong>arbitration / informal dispute</strong> program before or instead of court, when the manufacturer maintains one that complies with federal/state rules.</li>
    </ul>

    <h2>4. Buyer Documentation Checklist</h2>
    <p class="legal">Keep and bring the following when contacting the manufacturer, state agency, or an attorney:</p>
    <div class="checkline"><span class="checkbox"></span><span>Purchase contract / buyer’s order and financing documents</span></div>
    <div class="checkline"><span class="checkbox"></span><span>Manufacturer’s written warranty and owner’s manual</span></div>
    <div class="checkline"><span class="checkbox"></span><span>All repair orders showing dates, mileage, complaints, and work performed</span></div>
    <div class="checkline"><span class="checkbox"></span><span>Record of days the vehicle was out of service for warranty repairs</span></div>
    <div class="checkline"><span class="checkbox"></span><span>Written notices sent to manufacturer/dealer (certified mail receipts if used)</span></div>
    <div class="checkline"><span class="checkbox"></span><span>Photos, videos, or inspection reports of the defect</span></div>
    <div class="checkline"><span class="checkbox"></span><span>Arbitration / BBB AUTO LINE / manufacturer program filings (if any)</span></div>
    <div class="checkline"><span class="checkbox"></span><span>Service contract / VSC documents (if purchased)</span></div>

    <h2>5. Important Limits</h2>
    <p class="legal">This notice is an educational disclosure prepared with Desking by EnvisionDMS.com for ${esc(
      ctx.stateName
    )}. It is <strong>not</strong> legal advice and does not expand or reduce your statutory rights. Statutes, deadlines, and agency contacts change — confirm the current text of ${esc(
      info.statute
    )} and procedures with ${esc(
      info.agency
    )} or qualified counsel. Report inaccurate library content to support@envisiondms.com.</p>
    ${signatures(ctx)}`;
}
